import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { ModalButtonWithForm } from 'components/common';
import { FormGroup, Select } from 'components/common/form';

import type { AddCompetencyProfilePinnedUsersReq } from 'shared-types/requests';
import type { GetCompetencyProfilesRes } from 'shared-types/responses';

interface Props {
  competencyProfiles: GetCompetencyProfilesRes['competencyProfiles'];
  onPinned: () => Promise<void>;
}

const ProfileCompetencyProfilePinsAdd = ({
  competencyProfiles,
  onPinned
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { user }
  } = useReduxState();

  const handleSubmit = async (values: { competencyProfileId: number }) => {
    try {
      const body: AddCompetencyProfilePinnedUsersReq = { userIds: [user.id] };

      await api.post(
        `/admin/competency-profiles/${values.competencyProfileId}/pinned-users`,
        body
      );
      dispatch.addNotice({
        text: messages.competencyProfilePinnedUsersPinSuccess,
        type: 'success'
      });
      await onPinned();
    } catch (err) {
      await dispatch.handleErrorDispatch(err);

      // keep the modal open with the selection
      return false;
    }
  };

  const validateRequired = (value: number | null) =>
    value ? undefined : messages.commonFieldRequired;

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="thumbtack"
      buttonIsDropdown={false}
      buttonSize="header"
      buttonText={messages.competencyProfilePin}
      modalActionText={messages.competencyProfilePin}
      modalTitle={messages.competencyProfilePin}
      modalWidth="medium"
      onSubmit={handleSubmit}
      pristineIsValid={false}
    >
      {(formState) => (
        <FormGroup
          label={messages.competencyProfile}
          labelHelpText={messages.competencyProfilePinHelpText}
          maxWidth="100%"
        >
          <Select
            error={formState.errors.competencyProfileId}
            field="competencyProfileId"
            id="profile-competency-profile-pins-add"
            isSearchable={true}
            options={competencyProfiles.map((competencyProfile) => ({
              label:
                competencyProfile.titleLocales?.[language] ||
                competencyProfile.title,
              value: competencyProfile.id
            }))}
            validate={validateRequired}
            validateOnChange={true}
          />
        </FormGroup>
      )}
    </ModalButtonWithForm>
  );
};

export default ProfileCompetencyProfilePinsAdd;
